import React from 'react';
import { BrainCircuit, Save, Shapes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useInterpreterCategories, useUpsertInterpreterCategory } from './api';
import type { InterpreterCategory } from './types';

function emptyCategory(): InterpreterCategory {
  return {
    id: '',
    display_name: '',
    unspsc_family: '',
    unspsc_segment: '',
    parent_id: '',
    level: 1,
    approved: true,
  };
}

export default function InterpreterSchemasTab() {
  const categoriesQ = useInterpreterCategories();
  const upsertMutation = useUpsertInterpreterCategory();
  const [draft, setDraft] = React.useState<InterpreterCategory>(emptyCategory());
  const [message, setMessage] = React.useState<string | null>(null);

  async function handleSave() {
    try {
      setMessage(null);
      await upsertMutation.mutateAsync(draft);
      setMessage(`Saved category ${draft.id}`);
      setDraft(emptyCategory());
    } catch (error: any) {
      setMessage(error?.message || 'Failed to save category');
    }
  }

  function loadCategory(category: InterpreterCategory) {
    setDraft({
      id: category.id,
      display_name: category.display_name,
      unspsc_family: category.unspsc_family || '',
      unspsc_segment: category.unspsc_segment || '',
      parent_id: category.parent_id || '',
      level: category.level || 1,
      approved: category.approved !== false,
    });
  }

  return (
    <div className="space-y-6 pt-4 animate-fade-in">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <BrainCircuit className="w-4 h-4 text-purple-600" />
            Schema Category
          </div>

          <div className="space-y-1">
            <div className="text-xs text-gray-500">ID</div>
            <Input value={draft.id} onChange={(event) => setDraft(prev => ({ ...prev, id: event.target.value }))} placeholder="soil_sensor" />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-gray-500">Display name</div>
            <Input value={draft.display_name} onChange={(event) => setDraft(prev => ({ ...prev, display_name: event.target.value }))} placeholder="Soil Sensor" />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-gray-500">UNSPSC segment</div>
            <Input value={draft.unspsc_segment || ''} onChange={(event) => setDraft(prev => ({ ...prev, unspsc_segment: event.target.value }))} />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-gray-500">UNSPSC family</div>
            <Input value={draft.unspsc_family || ''} onChange={(event) => setDraft(prev => ({ ...prev, unspsc_family: event.target.value }))} />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-gray-500">Parent ID</div>
            <Input value={draft.parent_id || ''} onChange={(event) => setDraft(prev => ({ ...prev, parent_id: event.target.value }))} />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-gray-500">Level</div>
            <Input
              type="number"
              min={0}
              value={draft.level || 1}
              onChange={(event) => setDraft(prev => ({ ...prev, level: Number(event.target.value || 1) }))}
            />
          </div>
          {message ? <div className="text-xs text-gray-600">{message}</div> : null}
          <Button onClick={handleSave} disabled={upsertMutation.isPending || !draft.id.trim() || !draft.display_name.trim()} className="w-full">
            <Save className="w-4 h-4 mr-2" />
            {upsertMutation.isPending ? 'Saving...' : 'Save Category'}
          </Button>
        </div>

        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Shapes className="w-4 h-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-gray-900">Registered Categories</h2>
          </div>
          {categoriesQ.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} className="h-14 rounded-lg bg-gray-50 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {(categoriesQ.data || []).map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => loadCategory(category)}
                  className="w-full text-left rounded-xl border border-gray-200 p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{category.display_name}</div>
                      <div className="text-xs text-gray-500 mt-1">{category.id}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-500">
                      Level {category.level || 1}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600">
                    <div>Segment: {category.unspsc_segment || '-'}</div>
                    <div>Family: {category.unspsc_family || '-'}</div>
                    <div>Parent: {category.parent_id || '-'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
